(() => {
  'use strict';

  const controllerKey = '__club13StudioHeroController';
  if (window[controllerKey]) {
    window[controllerKey].init(document);
    return;
  }

  const instances = new WeakMap();
  const asArray = (value) => Array.isArray(value) ? value : [];
  const shuffled = (items) => {
    const copy = [...items];
    for (let index = copy.length - 1; index > 0; index -= 1) {
      const swapIndex = Math.floor(Math.random() * (index + 1));
      [copy[index], copy[swapIndex]] = [copy[swapIndex], copy[index]];
    }
    return copy;
  };
  const imageUrlFor = (product, image) => {
    const width = image.clientWidth * (window.devicePixelRatio || 1);
    return product.image[width <= 480 ? '480' : width <= 960 ? '960' : '1400'];
  };
  const abortError = () => Object.assign(new Error('Hero image aborted'), {name: 'AbortError'});
  const loadIncoming = (product, currentImage, signal) => new Promise((resolve, reject) => {
    const image = document.createElement('img');
    let timeout = 0;
    let settled = false;
    const cleanup = () => {
      window.clearTimeout(timeout);
      image.onload = null;
      image.onerror = null;
      signal.removeEventListener('abort', onAbort);
    };
    const finish = (callback, value) => {
      if (settled) return;
      settled = true;
      cleanup();
      callback(value);
    };
    const onAbort = () => {
      image.src = '';
      finish(reject, abortError());
    };
    image.onload = () => image.decode ? image.decode().then(() => finish(resolve, image), () => finish(resolve, image)) : finish(resolve, image);
    image.onerror = () => finish(reject, new Error('Hero image failed'));
    timeout = window.setTimeout(() => finish(reject, new Error('Hero image timed out')), 8000);
    signal.addEventListener('abort', onAbort, {once: true});
    if (signal.aborted) {
      onAbort();
      return;
    }
    image.className = 'studio-home__image studio-home__image--incoming';
    image.srcset = `${product.image['480']} 480w, ${product.image['960']} 960w, ${product.image['1400']} 1400w`;
    image.sizes = '(min-width: 901px) 44vw, 100vw';
    image.alt = product.alt || product.title;
    image.setAttribute('aria-hidden', 'true');
    image.src = imageUrlFor(product, currentImage);
  });

  const setup = (media) => {
    if (instances.has(media)) return;

    const link = media.querySelector('[data-studio-hero-link]');
    let image = link?.querySelector('.studio-home__image');
    const caption = media.querySelector('[data-studio-hero-caption]');
    const feedSection = media.dataset.feedSection;
    if (!link || !image || !caption || !feedSection) return;

    const enabled = media.dataset.rotationEnabled === 'true';
    const interval = Math.max(4, Number(media.dataset.rotationSeconds) || 7) * 1000;
    const pauses = new Set();
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const controller = new AbortController();
    let products = [];
    let queue = [];
    let currentId = String(media.dataset.initialProductId || '');
    let timer = 0;
    let destroyed = false;
    let swapping = false;
    let settleTransition = () => {};
    let swapFrame = 0;
    let observer;

    const stopTimer = () => {
      window.clearTimeout(timer);
      timer = 0;
    };
    const setPause = (reason, paused) => {
      if (paused) pauses.add(reason); else pauses.delete(reason);
      schedule();
    };
    const refill = () => {
      queue = shuffled(products.filter((product) => String(product.id) !== currentId));
    };
    const nextProduct = () => {
      if (!queue.length) refill();
      return queue.shift() || null;
    };
    const schedule = () => {
      stopTimer();
      if (!enabled || products.length < 2 || pauses.size || destroyed) return;
      timer = window.setTimeout(() => show(nextProduct(), true), interval);
    };
    const waitForFade = (incoming, commit) => new Promise((resolve) => {
      if (reducedMotion.matches) {
        commit();
        resolve();
        return;
      }
      let timeout = 0;
      let settled = false;
      const finish = (immediate = false) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        incoming.removeEventListener('transitionend', onTransitionEnd);
        controller.signal.removeEventListener('abort', onAbort);
        if (immediate) {
          incoming.style.transition = 'none';
          incoming.style.opacity = '1';
        }
        commit();
        settleTransition = () => {};
        resolve();
      };
      const onAbort = () => finish(true);
      const onTransitionEnd = () => finish();
      settleTransition = () => finish(true);
      incoming.addEventListener('transitionend', onTransitionEnd);
      timeout = window.setTimeout(finish, 750);
      controller.signal.addEventListener('abort', onAbort, {once: true});
    });
    const show = async (product, automated = false) => {
      if (!product || swapping || String(product.id) === currentId || destroyed) {
        schedule();
        return;
      }
      swapping = true;
      try {
        const incoming = await loadIncoming(product, image, controller.signal);
        if (destroyed) return;
        if (automated && pauses.size) {
          queue.unshift(product);
          return;
        }
        link.append(incoming);
        swapFrame = window.requestAnimationFrame(() => {
          swapFrame = 0;
          if (!destroyed) incoming.classList.add('is-visible');
        });
        await waitForFade(incoming, () => {
          if (destroyed) return;
          currentId = String(product.id);
          link.href = product.url;
          caption.textContent = product.title;
        });
        if (destroyed) return;
        image.remove();
        image = incoming;
        image.removeAttribute('aria-hidden');
      } catch (_) {
        // Retain the server-rendered product when a feed request or image decode fails.
      } finally {
        swapping = false;
        schedule();
      }
    };
    const onVisibility = () => setPause('hidden', document.hidden);
    const onMotionChange = () => setPause('motion', reducedMotion.matches);
    const onFocusOut = (event) => setPause('focus', media.contains(event.relatedTarget));
    const onMouseEnter = () => { setPause('hover', true); settleTransition(); };
    const onMouseLeave = () => setPause('hover', false);
    const onFocusIn = () => { setPause('focus', true); settleTransition(); };
    const onPointerDown = () => settleTransition();
    const onFeed = async () => {
      try {
        let page = 1;
        let pages = 1;
        const items = [];
        while (page <= pages && page <= 100) {
          const url = new URL(window.location.href);
          url.searchParams.set('section_id', feedSection);
          url.searchParams.set('page', String(page));
          const response = await fetch(url, {signal: controller.signal, credentials: 'same-origin'});
          if (!response.ok) throw new Error('Hero feed failed');
          const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
          const payload = documentFragment.querySelector('[data-studio-hero-feed]')?.textContent;
          const data = payload ? JSON.parse(payload) : null;
          if (!data) throw new Error('Hero feed missing');
          const responsePage = Number(data.page);
          pages = Number(data.pages);
          if (!Number.isInteger(responsePage) || responsePage !== page || !Number.isInteger(pages) || pages < 1 || pages > 100) throw new Error('Hero feed pagination invalid');
          items.push(...asArray(data.products).filter((product) => product?.id && product.url && product.image?.['480']));
          page += 1;
        }
        if (page <= pages) throw new Error('Hero feed pagination exceeded');
        const seen = new Set();
        products = items.filter((product) => !seen.has(String(product.id)) && seen.add(String(product.id)));
        const initialCandidates = products.filter((product) => String(product.id) !== currentId);
        if (initialCandidates.length && !window.Shopify?.designMode && media.dataset.initialRandomized !== 'true') {
          await show(initialCandidates[Math.floor(Math.random() * initialCandidates.length)]);
        }
        refill();
        schedule();
      } catch (error) {
        if (error.name !== 'AbortError') schedule();
      }
    };

    media.addEventListener('mouseenter', onMouseEnter);
    media.addEventListener('mouseleave', onMouseLeave);
    media.addEventListener('focusin', onFocusIn);
    media.addEventListener('focusout', onFocusOut);
    media.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('visibilitychange', onVisibility);
    reducedMotion.addEventListener?.('change', onMotionChange);
    if (window.Shopify?.designMode) pauses.add('editor');
    if (reducedMotion.matches) pauses.add('motion');
    if ('IntersectionObserver' in window) pauses.add('offscreen');
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(([entry]) => setPause('offscreen', !entry.isIntersecting), {threshold: .1});
      observer.observe(media);
    }
    instances.set(media, () => {
      destroyed = true;
      stopTimer();
      controller.abort();
      settleTransition();
      window.cancelAnimationFrame(swapFrame);
      observer?.disconnect();
      media.removeEventListener('mouseenter', onMouseEnter);
      media.removeEventListener('mouseleave', onMouseLeave);
      media.removeEventListener('focusin', onFocusIn);
      media.removeEventListener('focusout', onFocusOut);
      media.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('visibilitychange', onVisibility);
      reducedMotion.removeEventListener?.('change', onMotionChange);
      instances.delete(media);
    });
    onVisibility();
    onFeed();
  };

  const mediasWithin = (root) => {
    if (!root || root.nodeType !== Node.ELEMENT_NODE) return [];
    const medias = root.matches?.('[data-studio-hero-rotation]') ? [root] : [];
    return medias.concat([...root.querySelectorAll?.('[data-studio-hero-rotation]') || []]);
  };
  const init = (root = document) => (root === document ? document.querySelectorAll('[data-studio-hero-rotation]') : mediasWithin(root)).forEach(setup);
  const destroy = (root) => mediasWithin(root).forEach((media) => instances.get(media)?.());

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
  window[controllerKey] = {init, destroy};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), {once: true});
  else init();
})();
